import moment from 'moment';
import * as yup from 'yup';
import { MAX_TOTAL_UPLOAD_SIZE } from 'utils/constantUtil';
import { isUnsupportedAttachmentFormat } from 'utils/attachmentFormatUtil';
import { ContactType } from 'views/ContactNoteForm/components/data/contactTypeOptions';

import { CommunicationChannelEndDateList } from './data/DialogOptions';

const CHAR_LIMIT = 350;
const characterLimitError = `The character limit for this field is ${CHAR_LIMIT}`;

const contactNoteDialogValidationSchema = (
  usedInBr: boolean,
  isPartner: boolean,
  isLE: boolean,
  isEmailHomeScreen = false,
) =>
  yup.object().shape({
    partner:
      usedInBr || isEmailHomeScreen
        ? yup.object().nullable()
        : yup.object().shape({
            partnerUId: yup.string().nullable().required('Please provide a partner'),
          }),

    relatedPartners: usedInBr
      ? yup.array().when('source', {
          is: 'MYTRAVEL',
          then: yup.array().nullable(),
          otherwise: yup
            .array()
            .transform((value: { value: string; type?: string }[] = []) =>
              value.filter((item) => item.value !== 'other'),
            )
            .of(yup.object())
            .min(1, 'Please select at least one related partner'),
        })
      : yup
          .array()
          .of(yup.object())
          .test('pi-inclusion', 'Please include at least one related partner', function () {
            const { relatedPartners, contactType } = this.parent;
            if (isLE && relatedPartners.length <= 1) {
              return false;
            }
            if (!isLE && relatedPartners.length === 0) {
              return false;
            }
            const hasPI = relatedPartners.find((item: { value: string; type: string }) => item.type === 'PI');
            const hasOther = relatedPartners.find((item: { value: string; type: string }) => item.value === 'other');
            if (hasPI || hasOther) {
              return true;
            }

            if (isEmailHomeScreen) {
              return true;
            }

            if (
              contactType &&
              ![ContactType.EMAIL, ContactType.LETTER, ContactType.FAX, ContactType.OTHER].includes(contactType)
            ) {
              return this.createError({ message: 'Please include at least one Private Individual Partner' });
            }

            return true;
          }),

    relatedBRs:
      usedInBr || !isPartner
        ? yup.array().nullable()
        : yup.array().of(yup.string()).min(1, 'Please select at least one related br'),
    additionalAttendees: yup.array().of(yup.string()).min(1, 'Please select at least one bank representative'),
    otherPartner: yup.string().when('relatedPartners', {
      is: (relatedPartners: { value: string }[]) => relatedPartners.map((item) => item.value).includes('other'),
      then: yup.string().required('Please provide client representative'),
    }),
    subject: yup.string().trim().max(CHAR_LIMIT, characterLimitError).required('Please provide a subject'),

    contactType: yup.string().nullable().required('Please select a contact type'),

    contactTypeOther: yup
      .string()
      .nullable()
      .when('contactType', {
        is: 'OTHER',
        then: yup.string().nullable().required('Please specify the other communication channel'),
      }),

    startDate: yup
      .date()
      .transform((value, originalValue) => {
        // Convert string to Date if it's a string
        return typeof originalValue === 'string' ? new Date(originalValue) : value;
      })
      .nullable()
      .test('custom-start-date', '', function () {
        const { source, contactType, startDate } = this.parent;

        if (!startDate) {
          return this.createError({
            message: CommunicationChannelEndDateList.includes(contactType)
              ? 'Please provide a valid start date'
              : 'Please provide a valid date',
          });
        }

        if (source === 'MYTRAVEL') {
          return true; // already required check passed above
        }

        const minDate = moment().subtract(45, 'days').toDate();

        if (startDate < minDate) {
          return this.createError({
            message: 'A contact note can be backdated up to 45 days',
          });
        }

        return true;
      }),
    startTime: yup
      .string()
      .nullable()
      .test(
        'start-time-validation',
        // This message will be overridden dynamically below
        'Invalid time',
        function () {
          const { contactType, startTime } = this.parent;

          // Skip validation if contactType is empty
          if (!contactType || contactType.trim() === '') return true;

          if (!startTime && CommunicationChannelEndDateList.includes(contactType)) {
            return this.createError({ message: 'Please provide a valid start time' });
          }
          return true;
        },
      ),
    endDate: yup
      .date()
      .nullable()
      .when('contactType', {
        is: (contactType: ContactType) => CommunicationChannelEndDateList.includes(contactType),
        then: yup.date().nullable().required('Please provide a valid end date'),
      })
      .test('is-after-start', 'End date must be the same or after start date', function (value) {
        const { startDate } = this.parent;
        return !startDate || !value || moment(value).isSameOrAfter(moment(startDate), 'day');
      }),
    endTime: yup
      .string()
      .nullable()
      .when('contactType', {
        is: (contactType: ContactType) => CommunicationChannelEndDateList.includes(contactType),
        then: yup.string().nullable().required('Please provide a valid end time'),
      })
      .test('is-after-start-time', 'End time must be after start time', function (value) {
        const { startDate, startTime, endDate } = this.parent;
        if (!startDate || !startTime || !endDate || !value) return true; // Skip validation if missing values

        const startDateTime = moment(`${moment(startDate).format('YYYY-MM-DD')} ${moment(startTime).format('HH:mm')}`);
        const endDateTime = moment(`${moment(endDate).format('YYYY-MM-DD')} ${moment(value).format('HH:mm')}`);

        // If endDate is the same as startDate, ensure endTime is after startTime
        if (moment(startDate).isSame(endDate, 'day')) {
          return endDateTime.isAfter(startDateTime);
        }

        return true;
      }),
    qualifiedClientContact: yup.string().nullable().required('Please provide the client contact context'),
    crossBorderActivity: yup.string().nullable().required('Please provide the cross border context'),

    locationCountry: yup
      .string()
      .nullable()
      .when(['crossBorderActivity'], {
        is: (crossBorderActivity: string) => crossBorderActivity === 'true',
        then: (schema) => schema.required('Please provide a country'),
      }),

    locationCity: yup.string().nullable(),

    topics: yup.array().of(yup.string()).min(1, 'Please select at least one topic'),

    topicOther: yup
      .string()
      .nullable()
      .when('topics', {
        is: (topics: string[] | undefined) => Array.isArray(topics) && topics.includes('OTHER'),
        then: (schema) => schema.required('Please specify the other topic'),
        otherwise: (schema) => schema.notRequired(),
      }),

    initiatedBy: yup.string().nullable().required('Please select the initiator'),

    notes: yup.string().trim().nullable().required('Please provide input into the notes field'),

    source: yup.string().nullable(),
    attachments: yup
      .array()
      .of(yup.object())
      .max(9, 'You can upload at most 9 documents')
      .test('total-file-size', 'Total file size must not exceed 12MB', (files) => {
        if (!files || files.length === 0) return true;

        const totalSize = files.reduce((sum, file) => {
          return sum + (file?.size || 0);
        }, 0);

        return totalSize <= MAX_TOTAL_UPLOAD_SIZE;
      })
      // Drag-and-drop notes are archived to OnDemand, which only accepts the configured formats, so
      // the user has to remove an unsupported attachment before saving. Applies to the files they
      // picked and to the attachments that came with the dropped email alike.
      .test('unsupported-format', 'Remove the attachments with an unsupported file format', function (files) {
        if (!files || files.length === 0) return true;
        if (!(this.parent as { dragAndDrop?: boolean })?.dragAndDrop) return true;

        return files.every((file) => {
          const attachment = file as { name?: string; description?: string };
          return !isUnsupportedAttachmentFormat(attachment?.name || attachment?.description);
        });
      }),
  });

export default contactNoteDialogValidationSchema;
